import React from "react";
import "./index.css";
import { AppHeader } from "./layout/AppHeader";
import WeddingCalculator from "./WeddingCalculator";
import Contractors from "./Contractors";
import Vignettes from "./Vignettes";
import { TodosPage } from "./TodosPage";
import { About } from "./About";
import { MainLayout } from "./layout/MainLayout";
import { useTranslation } from "react-i18next";
import {
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";


function App() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    document.title = "Latwe wesele";
  }, []);

  // Determine active page from route
  const activePage = React.useMemo(() => {
    if (location.pathname.startsWith("/calculator")) return "calculator";
    if (location.pathname.startsWith("/contractors")) return "contractors";
    if (location.pathname.startsWith("/vignettes")) return "vignettes";
    if (location.pathname.startsWith("/todos")) return "todos";
    if (location.pathname.startsWith("/about")) return "about";
    return "home";
  }, [location.pathname]);

  const setActivePage = (page) => {
    if (page === "home") navigate("/");
    if (page === "calculator") navigate("/calculator");
    if (page === "contractors") navigate("/contractors");
    if (page === "vignettes") navigate("/vignettes");
    if (page === "todos") navigate("/todos");
    if (page === "about") navigate("/about");
  };

  return (
    <MainLayout>
      <AppHeader activePage={activePage} setActivePage={setActivePage} />
      <div className="w-full max-w-5xl mx-auto mt-4">
        <Routes>
          <Route
            path="/"
            element={
              <div className="min-h-[70vh] flex flex-col justify-center gap-16 py-12 text-slate-800">
                {/* Hero section */}
                <section className="max-w-2xl space-y-4">
                  <p className="text-sm uppercase tracking-[0.2em] text-slate-400">
                    {t("home.badge", "Wedding planning, simplified")}
                  </p>
                  <h2 className="text-4xl md:text-5xl font-semibold tracking-tight leading-tight text-slate-900">
                    {t(
                      "home.heroTitle",
                      "Simple, modern planning for your perfect day",
                    )}
                  </h2>
                  <p className="text-base md:text-lg text-slate-600 max-w-xl">
                    {t(
                      "home.heroTagline",
                      "Free, make all memories perfect — keep your budget clear and your stress low with tools made for modern couples.",
                    )}
                  </p>
                </section>

                {/* Functionality cards */}
                <section className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  <button
                    type="button"
                    onClick={() => setActivePage("calculator")}
                    className="group text-left rounded-2xl border border-slate-200/80 bg-white/70 p-6 shadow-sm backdrop-blur-sm transition hover:-translate-y-1 hover:border-slate-300 hover:shadow-md"
                  >
                    <div className="mb-4 inline-flex items-center rounded-full bg-slate-900 text-white px-3 py-1 text-xs font-medium tracking-wide">
                      {t("home.cards.calculator.badge", "Budget tool")}
                    </div>
                    <h3 className="text-xl font-semibold tracking-tight text-slate-900 mb-2">
                      {t(
                        "home.cards.calculator.title",
                        "Wedding cost calculator",
                      )}
                    </h3>
                    <p className="text-sm text-slate-600 mb-4">
                      {t(
                        "home.cards.calculator.description",
                        "Estimate your total wedding budget in minutes — fixed costs, per‑person spending, and a clear summary in one place.",
                      )}
                    </p>
                    <span className="text-sm font-medium text-slate-900 inline-flex items-center gap-1">
                      {t("home.cards.calculator.cta", "Open calculator")}
                      <span
                        aria-hidden
                        className="transition-transform group-hover:translate-x-0.5"
                      >
                        →
                      </span>
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setActivePage("contractors")}
                    className="group text-left rounded-2xl border border-slate-200/80 bg-white/70 p-6 shadow-sm backdrop-blur-sm transition hover:-translate-y-1 hover:border-slate-300 hover:shadow-md"
                  >
                    <div className="mb-4 inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-3 py-1 text-xs font-medium tracking-wide">
                      {t("home.cards.contractors.badge", "Vendor overview")}
                    </div>
                    <h3 className="text-xl font-semibold tracking-tight text-slate-900 mb-2">
                      {t(
                        "home.cards.contractors.title",
                        "Contractors & vendors",
                      )}
                    </h3>
                    <p className="text-sm text-slate-600 mb-4">
                      {t(
                        "home.cards.contractors.description",
                        "Keep track of photographers, venues, music, and more — compare offers and never lose important details.",
                      )}
                    </p>
                    <span className="text-sm font-medium text-slate-900 inline-flex items-center gap-1">
                      {t("home.cards.contractors.cta", "Browse contractors")}
                      <span
                        aria-hidden
                        className="transition-transform group-hover:translate-x-0.5"
                      >
                        →
                      </span>
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setActivePage("vignettes")}
                    className="group text-left rounded-2xl border border-slate-200/80 bg-white/70 p-6 shadow-sm backdrop-blur-sm transition hover:-translate-y-1 hover:border-slate-300 hover:shadow-md"
                  >
                    <div className="mb-4 inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-3 py-1 text-xs font-medium tracking-wide">
                      {t("home.cards.vignettes.badge", "Details")}
                    </div>
                    <h3 className="text-xl font-semibold tracking-tight text-slate-900 mb-2">
                      {t("home.cards.vignettes.title", "Table vignettes & place cards")}
                    </h3>
                    <p className="text-sm text-slate-600 mb-4">
                      {t(
                        "home.cards.vignettes.description",
                        "Prepare names and table layout. Soon you will be able to generate simple, elegant place cards directly here.",
                      )}
                    </p>
                    <span className="text-sm font-medium text-slate-900 inline-flex items-center gap-1">
                      {t("home.cards.vignettes.cta", "Vignettes")}
                      <span
                        aria-hidden
                        className="transition-transform group-hover:translate-x-0.5"
                      >
                        →
                      </span>
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setActivePage("todos")}
                    className="group text-left rounded-2xl border border-slate-200/80 bg-white/70 p-6 shadow-sm backdrop-blur-sm transition hover:-translate-y-1 hover:border-slate-300 hover:shadow-md"
                  >
                    <div className="mb-4 inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-3 py-1 text-xs font-medium tracking-wide">
                      {t("home.cards.todos.badge", "Timeline")}
                    </div>
                    <h3 className="text-xl font-semibold tracking-tight text-slate-900 mb-2">
                      {t("home.cards.todos.title", "Wedding to-do list")}
                    </h3>
                    <p className="text-sm text-slate-600 mb-4">
                      {t(
                        "home.cards.todos.description",
                        "A clear checklist of the most important tasks before the wedding — downloadable as a clean PDF.",
                      )}
                    </p>
                    <span className="text-sm font-medium text-slate-900 inline-flex items-center gap-1">
                      {t("home.cards.todos.cta", "Open checklist")}
                      <span
                        aria-hidden
                        className="transition-transform group-hover:translate-x-0.5"
                      >
                        →
                      </span>
                    </span>
                  </button>
                </section>
              </div>
            }
          />
          <Route path="/calculator" element={<WeddingCalculator />} />
          <Route path="/calculator/:code" element={<WeddingCalculator />} />
          <Route path="/contractors" element={<Contractors />} />
          <Route path="/vignettes" element={<Vignettes />} />
          <Route path="/todos" element={<TodosPage />} />
          <Route path="/about" element={<About />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </MainLayout>
  );
}

export default App;
